import { Button } from 'devextreme-react';
import * as ExcelJS from 'exceljs';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

const CancelTransferModal = ({
    isOpen,
    onClose,
    onConfirm,
    selectedFile,
    onFileSelect,
    apiResponse,
    isLoading
}) => {
    const [error, setError] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [showAllWarnings, setShowAllWarnings] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setShowAllWarnings(false);
            setError(null);
            setWarnings([]);
            setIsClosing(false);
            setIsProcessing(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (apiResponse) {
            setIsProcessing(false);
            if (apiResponse.stats.failed === 0) {
                toast.success(`${apiResponse.stats.updated} adet transfer başarıyla iptal edildi`, {
                    position: "top-right",
                    autoClose: 3000
                });
            }
        }
    }, [apiResponse]);

    const validateExcel = useCallback(async (file) => {
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const workbook = new ExcelJS.Workbook();
                    await workbook.xlsx.load(e.target.result);
                    const worksheet = workbook.worksheets[0];

                    const headerRow = worksheet.getRow(1);
                    const headers = [];
                    headerRow.eachCell((cell) => {
                        headers.push(cell.value?.toString().toLowerCase());
                    });

                    if (!headers.includes('sase')) {
                        setError('Excel dosyasında "sase" kolonu bulunamadı');
                        onFileSelect(null);
                        return;
                    }

                    if (headers.length > 1) {
                        setError('Excel dosyasında sadece "sase" kolonu olmalıdır');
                        onFileSelect(null);
                        return;
                    }

                    const saseColumnIndex = headers.indexOf('sase') + 1;
                    const newWarnings = [];
                    let validChassisCount = 0;
                    let totalRows = 0;

                    worksheet.eachRow((row, rowNumber) => {
                        if (rowNumber > 1) {
                            const chassisNo = row.getCell(saseColumnIndex).value?.toString().trim();
                            if (chassisNo) {
                                totalRows++;
                                if (chassisNo.length !== 17) {
                                    newWarnings.push(`Satır ${rowNumber}: "${chassisNo}" - 17 karakter uzunluğunda değil`);
                                } else {
                                    validChassisCount++;
                                }
                            }
                        }
                    });

                    if (totalRows === 0) {
                        setError('Excel dosyasında iptal edilecek şase numarası bulunamadı');
                        onFileSelect(null);
                        return;
                    }

                    if (validChassisCount === 0) {
                        setError('Geçerli formatta şase numarası bulunamadı');
                        setWarnings(newWarnings);
                        onFileSelect(null);
                        return;
                    }

                    setWarnings(newWarnings);
                    setError(null);
                    onFileSelect(file);

                } catch (error) {
                    setError('Excel dosyası okunurken bir hata oluştu');
                    onFileSelect(null);
                }
            };
            reader.onerror = () => {
                setError('Dosya okuma hatası');
                onFileSelect(null);
            };
            reader.readAsArrayBuffer(file);
        } catch (error) {
            setError('Beklenmeyen bir hata oluştu');
            onFileSelect(null);
        }
    }, [onFileSelect]);

    const handleExcelUpload = useCallback((event) => {
        if (!event?.target?.files || isLoading || isClosing) return;
        const file = event.target.files[0];
        if (!file) return;
        validateExcel(file);
    }, [isLoading, validateExcel, isClosing]);

    const handleClearFile = useCallback(() => {
        if (isLoading || isClosing) return;
        onFileSelect(null);
        setError(null);
        setWarnings([]);
    }, [isLoading, onFileSelect, isClosing]);

    const handleConfirmClick = useCallback(() => {
        if (isLoading || isClosing || isProcessing) return;
        setIsProcessing(true);
        setError(null);
        try {
            onConfirm();
        } catch (error) {
            setIsProcessing(false);
        }
    }, [isLoading, onConfirm, isClosing, isProcessing]);

    const handleCloseClick = useCallback(() => {
        if (isLoading || isClosing || isProcessing) return;
        setIsClosing(true);
        try {
            handleClearFile();
            onClose();
        } catch (error) {
            setIsClosing(false);
        }
    }, [isLoading, onClose, handleClearFile, isClosing, isProcessing]);

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            size="lg"
            backdrop="static"
            keyboard={false}
            unmountOnClose={true}
            className="cancel-transfer-modal"
            contentClassName="cancel-transfer-modal-content"
            zIndex={1050}
        >
            <ModalHeader>
                Transfer İptali
            </ModalHeader>
            <ModalBody className="p-4">
                <div className="mb-4">
                    {/* Excel Yükleme Alanı */}
                    <div className="mb-3">
                        <div className="mb-2" style={{ fontSize: '0.9rem', color: '#444' }}>
                            Excel Dosyası Yükleme
                        </div>
                        <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #eee' }}>
                            <div className="mb-1" style={{ fontSize: '0.85rem', color: '#666' }}>
                                • Excel dosyasında <b>sadece</b> "sase" isimli bir kolon bulunmalıdır
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                • Şase numaraları 17 karakter uzunluğunda olmalıdır
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-3">
                            <Button
                                onClick={() => document.getElementById('excelUpload').click()}
                                disabled={isLoading}
                                style={{
                                    backgroundColor: 'white',
                                    border: '1px solid #dee2e6',
                                    color: '#333',
                                    padding: '8px 16px',
                                    fontSize: '0.9rem'
                                }}
                            >
                                Excel Dosyası Seç
                            </Button>
                            {selectedFile && (
                                <div className="d-flex align-items-center gap-2">
                                    <span style={{ fontSize: '0.85rem', color: '#666' }}>
                                        {selectedFile.name}
                                    </span>
                                    <button
                                        onClick={handleClearFile}
                                        disabled={isLoading}
                                        className="btn btn-link p-0 ms-2"
                                        style={{ fontSize: '1.2rem', color: '#999' }}
                                    >
                                        ×
                                    </button>
                                </div>
                            )}
                        </div>

                        <input
                            type="file"
                            id="excelUpload"
                            accept=".xlsx,.xls"
                            onChange={handleExcelUpload}
                            style={{ display: 'none' }}
                            disabled={isLoading}
                        />
                    </div>

                    {/* Hata ve Uyarılar */}
                    {error && (
                        <Alert color="danger" className="mb-3 py-2">
                            <div className="d-flex align-items-center gap-2">
                                <i className="fa fa-exclamation-circle"></i>
                                <span style={{ fontSize: '0.85rem' }}>{error}</span>
                            </div>
                        </Alert>
                    )}

                    {warnings.length > 0 && (
                        <Alert color="warning" className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div style={{ fontSize: '0.85rem' }}>
                                    Uyarılar ({warnings.length})
                                </div>
                                {warnings.length > 3 && (
                                    <Button
                                        size="sm"
                                        onClick={() => setShowAllWarnings(prev => !prev)}
                                        style={{
                                            backgroundColor: 'transparent',
                                            border: '1px solid #ffc107',
                                            color: '#856404',
                                            padding: '2px 8px',
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        {showAllWarnings ? 'Daha Az' : 'Tümü'}
                                    </Button>
                                )}
                            </div>
                            <div style={{
                                maxHeight: '150px',
                                overflowY: 'auto',
                                fontSize: '0.8rem'
                            }}>
                                {(showAllWarnings ? warnings : warnings.slice(0, 3)).map((warning, index) => (
                                    <div key={index} className="mb-1">{warning}</div>
                                ))}
                                {!showAllWarnings && warnings.length > 3 && (
                                    <div className="mt-1 text-muted">
                                        ... ve {warnings.length - 3} uyarı daha
                                    </div>
                                )}
                            </div>
                        </Alert>
                    )}

                    {/* API Yanıtı */}
                    {apiResponse && (
                        <div className="mb-3">
                            {apiResponse.results?.some(r => !r.success) && (
                                <div style={{
                                    maxHeight: '250px',
                                    overflowY: 'auto',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    backgroundColor: '#fff'
                                }}>
                                    <div style={{
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #dee2e6',
                                        backgroundColor: '#f8f9fa'
                                    }}>
                                        <div style={{ fontSize: '0.9rem', color: '#dc3545', fontWeight: 500, marginBottom: '8px' }}>
                                            İptal Edilemeyen Transfer Kayıtları
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                            <span style={{ color: '#198754' }}>{apiResponse.stats.updated}</span> başarılı,
                                            <span style={{ color: '#dc3545', marginLeft: '4px' }}>{apiResponse.stats.failed}</span> başarısız işlem
                                        </div>
                                    </div>
                                    <div style={{ padding: '12px 16px' }}>
                                        <div className="d-flex mb-3" style={{
                                            fontSize: '0.85rem',
                                            color: '#495057',
                                            fontWeight: 600,
                                            borderBottom: '2px solid #dee2e6',
                                            paddingBottom: '8px'
                                        }}>
                                            <div style={{ width: '180px' }}>Şasi No</div>
                                            <div>Hata Mesajı</div>
                                        </div>
                                        {apiResponse.results
                                            .filter(r => !r.success)
                                            .map((result, index) => (
                                                <div
                                                    key={index}
                                                    className="d-flex mb-2"
                                                    style={{
                                                        fontSize: '0.85rem',
                                                        color: '#495057',
                                                        padding: '6px 0',
                                                        borderBottom: '1px solid #f1f3f5'
                                                    }}
                                                >
                                                    <div style={{ width: '180px', fontWeight: 500 }}>{result.chassis_no}</div>
                                                    <div style={{ color: '#666' }}>{result.error}</div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Butonlar */}

            </ModalBody>
            <ModalFooter>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '16px' }}>

                    <div style={{ width: '25%' }}>
                        <button
                            type="button"
                            className={`btn w-100 ${selectedFile ? 'btn-primary' : 'btn-info'}`}
                            onClick={handleConfirmClick}
                            disabled={!selectedFile || isLoading || isProcessing}
                        >
                            {isLoading || isProcessing ? (
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                    <span className="spinner-border spinner-border-sm"></span>
                                    <span>İşleniyor</span>
                                </div>
                            ) : 'Transfer Emri İptal'}
                        </button>


                    </div>

                    <div style={{ width: '25%' }}>
                        <button
                            type="button"
                            className="btn btn-outline-danger w-100"
                            onClick={onClose}
                            disabled={isLoading || isProcessing}
                        >
                            Vazgeç
                        </button>
                    </div>
                </div>
            </ModalFooter>
        </Modal>
    );
};

CancelTransferModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    selectedFile: PropTypes.object,
    onFileSelect: PropTypes.func.isRequired,
    apiResponse: PropTypes.object,
    isLoading: PropTypes.bool
};

CancelTransferModal.defaultProps = {
    isLoading: false
};

export default CancelTransferModal; 